import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Security() {
  const [activeTab, setActiveTab] = useState(0);

  const securityPillars = [
    {
      id: 0,
      title: 'Data Protection & Privacy',
      icon: 'ri-shield-check-line',
      color: 'from-[#22577A] to-[#38a3a5]',
      features: [
        {
          title: 'End-to-End Encryption',
          description: 'All data transmitted between your devices and our servers is protected with AES-256 encryption, the same standard used by financial institutions.',
        },
        {
          title: 'Data Residency Controls',
          description: 'Choose where your content and analytics data is stored with our multi-region infrastructure across North America, Europe, and Asia-Pacific.',
        },
        {
          title: 'Privacy by Design',
          description: 'We collect only the minimum data necessary to provide our services. Your social media content remains yours, and we never share it with third parties.',
        },
        {
          title: 'GDPR & CCPA Compliance',
          description: 'Full compliance with global privacy regulations including GDPR, CCPA, and other regional data protection laws.',
        },
      ],
    },
    {
      id: 1,
      title: 'Application Security',
      icon: 'ri-lock-line',
      color: 'from-[#38a3a5] to-[#66b3ff]',
      features: [
        {
          title: 'Secure Development Lifecycle',
          description: 'Our engineering team follows industry best practices with security integrated into every stage of development, from design to deployment.',
        },
        {
          title: 'Regular Security Audits',
          description: 'Third-party penetration testing and security assessments conducted quarterly to identify and address potential vulnerabilities.',
        },
        {
          title: 'Automated Vulnerability Scanning',
          description: 'Continuous monitoring of our codebase and dependencies to detect and patch security issues before they can be exploited.',
        },
        {
          title: 'Multi-Factor Authentication',
          description: 'Protect your account with optional 2FA using authenticator apps, SMS, or hardware security keys.',
        },
      ],
    },
    {
      id: 2,
      title: 'Infrastructure & Cloud Security',
      icon: 'ri-cloud-line',
      color: 'from-[#66b3ff] to-[#b3d9ff]',
      features: [
        {
          title: 'Enterprise-Grade Cloud Infrastructure',
          description: 'Built on AWS and Google Cloud Platform with 99.9% uptime SLA, redundant systems, and automatic failover.',
        },
        {
          title: 'Network Security',
          description: 'Advanced firewalls, intrusion detection systems, and DDoS protection safeguard our infrastructure 24/7.',
        },
        {
          title: 'Regular Backups',
          description: 'Automated daily backups with point-in-time recovery capabilities ensure your data is never lost.',
        },
        {
          title: 'Disaster Recovery Plan',
          description: 'Comprehensive business continuity procedures tested regularly to ensure rapid recovery from any incident.',
        },
      ],
    },
    {
      id: 3,
      title: 'Compliance & Certifications',
      icon: 'ri-file-shield-line',
      color: 'from-[#b3d9ff] to-[#e4f4ff]',
      features: [
        {
          title: 'SOC 2 Type II Certified',
          description: 'Annual audits verify our controls for security, availability, processing integrity, confidentiality, and privacy.',
        },
        {
          title: 'ISO 27001 Compliant',
          description: 'Information security management system aligned with international standards for protecting customer data.',
        },
        {
          title: 'GDPR & Privacy Shield',
          description: 'Full compliance with EU General Data Protection Regulation and cross-border data transfer frameworks.',
        },
        {
          title: 'Social Media Platform Security',
          description: 'OAuth 2.0 authentication with major platforms ensures secure access without storing your passwords.',
        },
      ],
    },
    {
      id: 4,
      title: 'Threat Detection & Response',
      icon: 'ri-alarm-warning-line',
      color: 'from-[#22577A] to-[#66b3ff]',
      features: [
        {
          title: '24/7 Security Monitoring',
          description: 'Real-time threat detection and monitoring with automated alerts for suspicious activities across all systems.',
        },
        {
          title: 'Incident Response Team',
          description: 'Dedicated security professionals ready to respond to incidents with documented procedures and communication protocols.',
        },
        {
          title: 'Threat Intelligence',
          description: 'Integration with global threat databases and security communities to stay ahead of emerging risks.',
        },
        {
          title: 'Security Incident Transparency',
          description: 'Commitment to timely disclosure and communication in the unlikely event of a security incident affecting user data.',
        },
      ],
    },
    {
      id: 5,
      title: 'Employee Security Culture',
      icon: 'ri-team-line',
      color: 'from-[#38a3a5] to-[#b3d9ff]',
      features: [
        {
          title: 'Security Training Program',
          description: 'All employees complete security awareness training during onboarding and annual refresher courses.',
        },
        {
          title: 'Background Checks',
          description: 'Comprehensive background verification for all team members with access to production systems or customer data.',
        },
        {
          title: 'Least Privilege Access',
          description: 'Role-based access controls ensure employees only have permissions necessary for their specific job functions.',
        },
        {
          title: 'Secure Development Practices',
          description: 'Engineering team trained in OWASP Top 10, secure coding standards, and responsible AI development.',
        },
      ],
    },
  ];

  const certifications = [
    {
      name: 'SOC 2 Type II',
      icon: 'ri-award-line',
      status: 'Certified',
      color: 'from-blue-500 to-blue-600',
    },
    {
      name: 'ISO 27001',
      icon: 'ri-shield-check-line',
      status: 'Compliant',
      color: 'from-green-500 to-green-600',
    },
    {
      name: 'GDPR',
      icon: 'ri-global-line',
      status: 'Compliant',
      color: 'from-purple-500 to-purple-600',
    },
    {
      name: 'CCPA',
      icon: 'ri-file-shield-line',
      status: 'Compliant',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#66b3ff] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full font-medium mb-6">
              <i className="ri-shield-check-line mr-2"></i>
              Trust & Security
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              Your Data Security is
              <span className="block text-[#e4f4ff]">Our Top Priority</span>
            </h1>

            <p className="text-xl lg:text-2xl text-white/90 mb-10 leading-relaxed">
              MandaPost is built on a foundation of enterprise-grade security, privacy by design, and transparent practices to protect your content and data.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#security-overview"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-file-download-line"></i>
                Download Security Overview
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                Contact Security Team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Security Stats */}
      <section className="py-12 bg-white border-b-2 border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22577A] mb-2">99.9%</div>
              <div className="text-gray-600">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22577A] mb-2">24/7</div>
              <div className="text-gray-600">Security Monitoring</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22577A] mb-2">AES-256</div>
              <div className="text-gray-600">Encryption Standard</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#22577A] mb-2">SOC 2</div>
              <div className="text-gray-600">Type II Certified</div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Pillars - Tabbed Interface */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
              Comprehensive Security Framework
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Six pillars of security protecting your social media content, data, and account access.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            {securityPillars.map((pillar) => (
              <button
                key={pillar.id}
                onClick={() => setActiveTab(pillar.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  activeTab === pillar.id
                    ? `bg-gradient-to-r ${pillar.color} text-white shadow-lg scale-105`
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#66b3ff]'
                }`}
              >
                <i className={pillar.icon}></i>
                <span className="hidden sm:inline">{pillar.title}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 lg:p-12 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className={`w-16 h-16 bg-gradient-to-br ${securityPillars[activeTab].color} rounded-xl flex items-center justify-center shadow-lg`}>
                <i className={`${securityPillars[activeTab].icon} text-white text-3xl`}></i>
              </div>
              <h3 className="text-3xl font-bold text-[#22577A]">{securityPillars[activeTab].title}</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {securityPillars[activeTab].features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10 rounded-xl p-6 border-2 border-gray-100 hover:border-[#66b3ff] hover:shadow-md transition-all"
                >
                  <h4 className="text-xl font-bold text-[#22577A] mb-3 flex items-center gap-2">
                    <i className="ri-check-line text-[#66b3ff]"></i>
                    {feature.title}
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
              Certifications & Compliance
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Independently verified security and compliance standards you can trust.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border-2 border-gray-200 hover:border-[#66b3ff] p-8 text-center hover:shadow-xl transition-all"
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${cert.color} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <i className={`${cert.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A] mb-2">{cert.name}</h3>
                <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                  {cert.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Best Practices for Users */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
              Protect Your Account
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Security is a shared responsibility. Follow these best practices to keep your MandaPost account secure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-key-2-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#22577A] mb-4">Use Strong Passwords</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Create unique passwords with 12+ characters</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Use a password manager for secure storage</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Never reuse passwords across services</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#38a3a5] to-[#66b3ff] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#22577A] mb-4">Enable 2FA</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Activate two-factor authentication</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Use authenticator apps over SMS</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Keep backup codes in a safe place</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-[#66b3ff] to-[#b3d9ff] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#22577A] mb-4">Stay Vigilant</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Review account activity regularly</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Be cautious of phishing emails</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#66b3ff] mt-1"></i>
                  <span>Report suspicious activity immediately</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Security Team */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-2xl p-8 lg:p-12 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-customer-service-line text-3xl"></i>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Security Questions or Concerns?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Our security team is here to help. Report vulnerabilities, request security documentation, or ask questions about our practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:security@mandapost.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                security@mandapost.com
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-questionnaire-line"></i>
                Contact Form
              </a>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-sm text-white/80">
                Responsible Disclosure: If you've discovered a security vulnerability, please email us at{' '}
                <a href="mailto:security@mandapost.com" className="underline font-semibold">
                  security@mandapost.com
                </a>
                {' '}with details. We take all reports seriously and will respond within 48 hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
